/** @typedef {import("../../api/types").ProviderConfig} ProviderConfig */
import React, {useEffect, useState} from "react";
import axios from "axios";
import AddressLink from "../../shared/components/AddressLink";
import Field from "../../shared/components/Field";
import IconLabel from "../../shared/components/IconLabel";
import {
    CashIcon,
    ClockIcon,
    FileTextIcon,
    GlobeIcon,
    KeyIcon,
    PaletteIcon,
    PencilIcon,
    PixIcon,
    ShopIcon,
} from "../../shared/components/icons";
import LookPicker from "../../shared/components/LookPicker";
import ThemePicker from "../../shared/components/ThemePicker";
import {formatDays, formatTime} from "../../shared/utils/format";
import {config as configResource} from "../resources/resources";
import {Link, useLocation} from "react-router-dom";
import {Alert, Badge, Button, Card, CardBody, CardHeader, Container, Spinner} from "reactstrap";

const CONFIG_URL = "/os/api/provider_config/";

// What the page shows, grouped. `format` turns the API value into what to
// read; `icon` goes before the value; `hint` explains it in a tooltip.
/** @typedef {{key: string, label: string, format?: (value: any) => string, icon?: React.ComponentType, hint?: string, wide?: boolean, multiline?: boolean, render?: (text: string, config: ProviderConfig) => React.ReactNode}} FieldSpec */
/** @type {{title: string, icon: React.ComponentType, fields: FieldSpec[]}[]} */
const SECTIONS = [
    {
        title: "Empresa",
        icon: ShopIcon,
        fields: [
            {
                key: "showcase_name",
                label: "Nome da vitrine",
                hint: "O endereço da sua página pública, para compartilhar com os clientes. Não pode ser alterado.",
                // opens the public page (/p/<name>) in a new tab
                render: (text) => (
                    <a
                        href={`/p/${encodeURIComponent(text)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Abrir a página pública"
                        className="text-break"
                    >
                        {text}
                    </a>
                ),
            },
            {key: "name_on_receipt", label: "Nome no recibo"},
            {
                key: "address",
                label: "Endereço",
                // a Google Maps link; the city (below) narrows the search
                render: (text, c) => <AddressLink address={text} area={c.city} />,
            },
            {key: "city", label: "Cidade"},
            {
                key: "about",
                label: "Sobre",
                hint: "Aparece na sua página pública, para os clientes conhecerem o seu trabalho.",
                // a paragraph or more: the whole row, keeping its line breaks
                wide: true,
                multiline: true,
            },
        ],
    },
    {
        title: "Pagamento",
        icon: CashIcon,
        fields: [{key: "pix_key", label: "Chave Pix", icon: PixIcon}],
    },
    {
        title: "Região",
        icon: GlobeIcon,
        fields: [
            {key: "language", label: "Idioma", format: (v) => optionLabel("language", v)},
            {key: "currency", label: "Moeda", format: (v) => optionLabel("currency", v)},
            {key: "timezone", label: "Fuso horário", format: (v) => v.replace(/_/g, " ")},
        ],
    },
    {
        title: "Atendimento",
        icon: ClockIcon,
        fields: [
            {key: "opening_time", label: "Horário de abertura", format: formatTime},
            {key: "closing_time", label: "Horário de fechamento", format: formatTime},
        ],
    },
    {
        title: "Orçamentos",
        icon: FileTextIcon,
        fields: [
            {
                key: "quote_expiration",
                label: "Validade",
                format: formatDays,
                hint: "Por quantos dias o cliente pode confirmar ou cancelar uma ordem enviada.",
            },
            {
                key: "quote_availability",
                label: "Disponibilidade",
                format: formatDays,
                hint: "Por quantos dias, depois da validade, o cliente ainda pode ver a ordem pelo link.",
            },
        ],
    },
];

/** The label the edit form shows for a choice (e.g. "pt-br" -> "Português (Brasil)"). */
function optionLabel(field, value) {
    const options = configResource.fields.find((f) => f.name === field)?.options;
    return options?.find((o) => o.value === value)?.label ?? String(value);
}

const Config = () => {
    // PasswordChange sends the user back here with this note.
    const passwordChanged = Boolean(useLocation().state?.passwordChanged);
    const [config, setConfig] = useState(/** @type {ProviderConfig|null} */ (null));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const controller = new AbortController();

        // One config per provider: the list holds at most one entry.
        axios
            .get(CONFIG_URL, {signal: controller.signal})
            .then(({data}) => {
                const [first] = data.results ?? [];
                if (first) {
                    setConfig(first);
                } else {
                    setError("Nenhuma configuração encontrada.");
                }
            })
            .catch((err) => {
                if (axios.isCancel(err)) return;
                setError("Não foi possível carregar as configurações.");
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });

        return () => controller.abort();
    }, []);

    return (
        <Container className="mt-4 mb-5">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <h3 className="mb-0">Configurações</h3>
                <div className="d-flex gap-2">
                    <Button
                        color="secondary"
                        outline
                        size="sm"
                        className="text-nowrap"
                        tag={Link}
                        to="/config/password"
                    >
                        <IconLabel icon={<KeyIcon />}>Alterar senha</IconLabel>
                    </Button>
                    {config && (
                        <Button color="primary" outline size="sm" className="text-nowrap" tag={Link} to="/config/edit">
                            <IconLabel icon={<PencilIcon size={16} />}>Editar</IconLabel>
                        </Button>
                    )}
                </div>
            </div>
            {passwordChanged && <Alert color="success">Senha alterada com sucesso.</Alert>}
            {loading && (
                <div className="text-center">
                    <Spinner />
                </div>
            )}
            {error && <Alert color="danger">{error}</Alert>}
            {config &&
                SECTIONS.map(({title, icon: SectionIcon, fields}) => (
                    <Card className="mb-3" key={title}>
                        <CardHeader>
                            <IconLabel icon={<SectionIcon />}>{title}</IconLabel>
                        </CardHeader>
                        <CardBody>
                            <div className="row g-3">
                                {fields.map(({key, label, format, icon: FieldIcon, hint, render, wide, multiline}) => {
                                    const raw = config[key];
                                    const text =
                                        raw === null || raw === undefined || raw === ""
                                            ? ""
                                            : String(format ? format(raw) : raw);
                                    return (
                                        <div className={wide ? "col-12" : "col-12 col-md-6"} key={key}>
                                            <Field
                                                label={label}
                                                value={text}
                                                info={hint}
                                                preserveLineBreaks={multiline}
                                            >
                                                {render ? (
                                                    render(text, config)
                                                ) : (
                                                    <span className="d-inline-flex align-items-center gap-2">
                                                        {FieldIcon && <FieldIcon />}
                                                        {text}
                                                    </span>
                                                )}
                                            </Field>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardBody>
                    </Card>
                ))}
            <Card className="mb-3">
                <CardHeader>
                    <IconLabel icon={<PaletteIcon />}>Aparência</IconLabel>
                </CardHeader>
                <CardBody>
                    <div className="row g-4">
                        <div className="col-12 col-md-6">
                            <h6 className="mb-2">Cor</h6>
                            <ThemePicker
                                disabled={!config}
                                save={(theme) => axios.patch(`${CONFIG_URL}${config?.id}/`, {theme})}
                                onSaveError={() => setError("Não foi possível salvar a cor. Tente novamente.")}
                            />
                            <div className="small text-muted mt-3">
                                A cor muda na hora, no topo e nos botões, e fica salva na sua conta. Seus clientes
                                também a veem nas páginas públicas.
                            </div>
                        </div>
                        <div className="col-12 col-md-6">
                            <div className="small text-muted mb-2">Prévia</div>
                            <div className="d-flex flex-wrap align-items-center gap-3" aria-label="Prévia da cor">
                                <Button color="primary" size="sm" type="button">
                                    Botão
                                </Button>
                                <Button color="primary" size="sm" outline type="button">
                                    Botão
                                </Button>
                                <Badge pill color="primary">
                                    Confirmada
                                </Badge>
                                <span className="link-primary text-decoration-underline">Link</span>
                            </div>
                        </div>
                    </div>
                    <hr />
                    <h6 className="mb-2">Estilo</h6>
                    <LookPicker
                        disabled={!config}
                        save={(look) => axios.patch(`${CONFIG_URL}${config?.id}/`, {look})}
                        onSaveError={() => setError("Não foi possível salvar o estilo. Tente novamente.")}
                    />
                    <div className="small text-muted mt-1">
                        O estilo muda a aparência de todas as telas e fica salvo na sua conta. Seus clientes sempre veem
                        o estilo padrão.
                    </div>
                </CardBody>
            </Card>
        </Container>
    );
};

export default Config;
